import React, { useCallback } from 'react';
import styled from 'styled-components';
import type { TurnStatus } from '../../../hooks/useWorkspace';

const Footer = styled.footer`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  opacity: 0;
  transition: opacity 0.15s;
  min-height: 1.75rem;
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.125rem;
`;

const ActionBtn = styled.button`
  padding: 0.25rem 0.5rem;
  border: none;
  background: transparent;
  color: var(--text-muted);
  font-family: inherit;
  font-size: 0.75rem;
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;

  &:hover {
    background: var(--bg-elevated);
    color: var(--text-primary);
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

interface TurnFooterProps {
  turnId: string;
  assistantContent: string;
  status: TurnStatus;
  onRegenerate?: (turnId: string) => void;
  onOpenInAnalyze?: (turnId: string) => void;
  onFeedback?: (turnId: string, rating: 'good' | 'bad') => void;
}

/**
 * Hover-visible turn footer. Copy / Regenerate / Good / Bad / Open-in-Analyze.
 * Mirrors the prototype's row that appears beneath each assistant response.
 */
const TurnFooter: React.FC<TurnFooterProps> = ({
  turnId,
  assistantContent,
  status,
  onRegenerate,
  onOpenInAnalyze,
  onFeedback,
}) => {
  const canAct = status === 'completed' || status === 'failed';

  const handleCopy = useCallback(() => {
    if (!assistantContent) return;
    try {
      void navigator.clipboard.writeText(assistantContent);
    } catch {
      /* best effort */
    }
  }, [assistantContent]);

  return (
    <Footer className="turn-footer">
      <Actions>
        <ActionBtn
          type="button"
          onClick={handleCopy}
          disabled={!assistantContent}
          title="Copy response"
        >
          Copy
        </ActionBtn>
        <ActionBtn
          type="button"
          onClick={() => onRegenerate?.(turnId)}
          disabled={!canAct || !onRegenerate}
          title="Regenerate response"
        >
          Regenerate
        </ActionBtn>
        <ActionBtn
          type="button"
          onClick={() => onFeedback?.(turnId, 'good')}
          disabled={!canAct || !onFeedback}
          title="Mark helpful"
          aria-label="Mark helpful"
        >
          Good
        </ActionBtn>
        <ActionBtn
          type="button"
          onClick={() => onFeedback?.(turnId, 'bad')}
          disabled={!canAct || !onFeedback}
          title="Mark unhelpful"
          aria-label="Mark unhelpful"
        >
          Bad
        </ActionBtn>
        <ActionBtn
          type="button"
          onClick={() => onOpenInAnalyze?.(turnId)}
          disabled={!canAct || !onOpenInAnalyze}
          title="Open provenance in Analyze tab"
        >
          Open in Analyze
        </ActionBtn>
      </Actions>
    </Footer>
  );
};

export default TurnFooter;
